import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, LayoutGrid, Map as MapIcon, ShoppingBag } from 'lucide-react';
import {
  FURNITURE_BY_ID,
  HOME_ACTIVITIES,
  HOME_PLACE,
  homeUse,
  NEEDS,
  SELL_BACK_SHARE,
  formatPence,
  placementProblem,
  type GuestView,
  type FurnitureItem,
  type LifeView,
  type MyHomeReply,
  type Needs,
  type OwnedThing,
  type PlayState,
  type Rotation,
} from '@london-life/contracts';
import { describeError, type FriendlyError } from '@london-life/errors';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { lookToCharacter, toPreviewCharacter } from '@london-life/game/appearance';
import type { RoomThing } from '@london-life/game/house/room';
import { api } from '../lib/api';
import { HouseView } from './house-view';
import { Catalogue, SelectedBar, freeSpot, placedOf } from './buy-panel';
import { notify } from '../lib/alerts';
import { useBackLayer } from '../lib/back-stack';
import { PersonPicture } from '../people/person-card';
import { useCatalogue } from '../lib/content';
import { actionKey, londonDay } from '../lib/format';

export type HouseOpen = 'welcome' | 'open' | 'shop';

// which thing at home does what: the bed sleeps, the telly (with a sofa) is telly time
const activityFor = (item: FurnitureItem) =>
  Object.entries(HOME_ACTIVITIES).find(([, rule]) => rule.uses === item.shape || rule.with === item.shape)?.[0] ?? null;

// The player's home, the way Lagos Life does it: the room fills the screen, the character walks where you tap
// and uses a thing when you tap it. The bar at the bottom has what you can do here and the rent. Buy mode
// shows the floor's grid and the catalogue: drag things about, turn them, store or sell them, and buy what
// you need. Every change is saved as you go.
export function HouseScreen({
  life,
  dataSaver,
  open,
  onMode,
  onClose,
  onWallet,
  play,
  needs,
  busyFor,
  onAct,
  onGoHome,
  guests = [],
  onSendHome,
}: {
  life: LifeView;
  dataSaver?: boolean;
  open: HouseOpen;
  // into Buy mode ('shop') and out of it ('open')
  onMode: (mode: 'open' | 'shop') => void;
  onClose: () => void;
  // money changed in the shop: the map's wallet refreshes
  onWallet?: () => void;
  // the live game: where the character is and how they feel, to use the furniture
  play?: PlayState | null;
  needs?: Needs | null;
  busyFor?: number;
  onAct?: (activityId: string) => Promise<boolean>;
  onGoHome?: () => Promise<boolean>;
  // visitors (Lagos Life's guests): those let in walk about the room; the host can say it is time to go
  guests?: GuestView[];
  onSendHome?: (visitId: string) => void;
}) {
  const buyMode = open === 'shop';
  const content = useCatalogue();
  const [reply, setReply] = useState<MyHomeReply | null>(null);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  const [draft, setDraft] = useState<RoomThing[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [catalogue, setCatalogue] = useState(true);
  const [homeTab, setHomeTab] = useState<'do' | 'rent' | 'guests' | null>(null);
  const inside = guests.filter((guest) => guest.status === 'in');
  const insideKey = inside.map((guest) => guest.visitId).join(',');
  const visitors = useMemo(
    () =>
      inside.map((guest) => ({
        id: guest.lifeId,
        character: lookToCharacter(guest.look, guest.firstName),
        tag: guest.username ? `@${guest.username}` : guest.firstName,
      })),
    // the same guests: the same people in the room
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [insideKey],
  );
  const [folded, setFolded] = useState(false);
  // a refused drop: the room is redrawn where things were
  const [revision, setRevision] = useState(0);
  const [selling, setSelling] = useState(false);
  const [using, setUsing] = useState(false);
  const accept = useCallback((next: MyHomeReply, message?: string) => {
    setReply(next);
    setDraft(placedOf(next.home.things));
    if (message) notify(message);
  }, []);
  const load = useCallback(() => {
    setFailure(null);
    api.home
      .get()
      .then((next) => accept(next))
      .catch((error: unknown) => setFailure(describeError(error)));
  }, [accept]);
  useEffect(load, [load]);
  const home = reply?.home;
  // what kind of home it is (its name, where, what it is like)
  const type = home ?? null;
  const stored = useMemo(
    () => home?.things.filter((thing) => !draft.some((entry) => entry.id === thing.id)) ?? [],
    [home, draft],
  );
  const selectedThing = draft.find((thing) => thing.id === selected) ?? null;
  const selectedItem = selectedThing ? FURNITURE_BY_ID.get(selectedThing.item) : null;
  const selectedOwned = home?.things.find((thing) => thing.id === selected) ?? null;
  // rent owed: the bar opens on the rent
  const owed = (home?.rent.owedPence ?? 0) > 0;
  useEffect(() => {
    if (owed) setHomeTab('rent');
  }, [owed]);

  const run = async (work: () => Promise<MyHomeReply & { message?: string }>, failed?: () => void, quiet = false) => {
    setBusy(true);
    setFailure(null);
    try {
      const next = await work();
      accept(next, quiet ? undefined : next.message);
      onWallet?.();
      return true;
    } catch (error) {
      setFailure(describeError(error));
      failed?.();
      return false;
    } finally {
      setBusy(false);
    }
  };
  // every change is saved straight away and quietly (only where each thing stands goes to the server)
  const commit = (next: RoomThing[]) => {
    setDraft(next);
    void run(
      () => api.home.arrange({ placed: next.map(({ id, x, z, rotation }) => ({ id, x, z, rotation })) }),
      () => setDraft(placedOf(home?.things ?? [])),
      true,
    );
  };
  const leaveBuy = () => {
    setSelected(null);
    setSelling(false);
    onMode('open');
  };
  useBackLayer(buyMode, leaveBuy);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (buyMode) leaveBuy();
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyMode, onClose]);

  // ---- arranging (checked here as the player goes, and again by the server when saved)
  const tryLayout = (next: RoomThing[], refused: string) => {
    if (!home) return false;
    const problem = placementProblem(home.room.floor, next);
    if (problem) {
      notify(problem || refused);
      setRevision((value) => value + 1);
      return false;
    }
    commit(next);
    return true;
  };
  const drop = (id: string, x: number, z: number) =>
    tryLayout(
      draft.map((thing) => (thing.id === id ? { ...thing, x, z } : thing)),
      'It does not fit there.',
    );
  const turn = () => {
    if (!selectedThing) return;
    const rotation = ((selectedThing.rotation + 1) % 4) as Rotation;
    tryLayout(
      draft.map((thing) => (thing.id === selectedThing.id ? { ...thing, rotation } : thing)),
      'No room to turn it there.',
    );
  };
  const store = () => {
    if (!selectedThing) return;
    commit(draft.filter((thing) => thing.id !== selectedThing.id));
    setSelected(null);
  };
  const place = (thing: { id: string; item: string }, layout = draft) => {
    const item = FURNITURE_BY_ID.get(thing.item);
    if (!home || !item) return;
    const spot = freeSpot(home.room.floor, layout, item);
    if (!spot) return notify(`No room for ${item.label}. Move something first.`);
    commit([...layout, { id: thing.id, item: item.id, ...spot, rotation: 0 }]);
    setSelected(thing.id);
  };
  const sell = (thing: OwnedThing) => {
    setSelling(false);
    setSelected(null);
    void run(() => api.home.sell(thing.id));
  };
  // buying puts it straight into the room, in the first clear spot, picked up so it can be moved
  const buy = async (item: FurnitureItem) => {
    const before = new Set(home?.things.map((thing) => thing.id));
    setBusy(true);
    setFailure(null);
    try {
      const next = await api.home.buy(item.id, actionKey('furniture'));
      accept(next, next.message);
      onWallet?.();
      const bought = next.home.things.find((thing) => !before.has(thing.id));
      if (bought && !bought.placed) place(bought, placedOf(next.home.things));
    } catch (error) {
      setFailure(describeError(error));
    } finally {
      setBusy(false);
    }
  };
  const payRent = () => void run(() => api.home.payRent(actionKey('rent')));

  // ---- using the furniture: the server does the activity; the wallet and needs on the map follow
  const atHome = play?.placeId === HOME_PLACE;
  const busyNow = (busyFor ?? 0) > 0;
  const standingItems = draft.flatMap((thing) => FURNITURE_BY_ID.get(thing.item) ?? []);
  // what can be done at home (the activities are published content; the furniture rules are the game's)
  const uses = Object.keys(HOME_ACTIVITIES).flatMap((activityId) => {
    const activity = content?.activity(activityId);
    return activity ? [{ activity, use: homeUse(activityId, standingItems)! }] : [];
  });
  const act = async (activityId: string) => {
    if (!onAct) return;
    setUsing(true);
    try {
      const ok = await onAct(activityId);
      if (ok) onWallet?.();
    } finally {
      setUsing(false);
    }
  };
  // a thing tapped in the room: the character walks over and uses it
  const useThing = (id: string) => {
    const thing = draft.find((entry) => entry.id === id);
    const item = thing && FURNITURE_BY_ID.get(thing.item);
    if (!item) return null;
    const activityId = activityFor(item);
    const entry = uses.find((option) => option.activity.id === activityId);
    if (!activityId || !entry) {
      notify(`${item.icon} ${item.label}`);
      return null;
    }
    if (!atHome) {
      notify('You are out. Come home to use your things.');
      return null;
    }
    if (using) return null;
    // doing something already: it waits its turn (as in Lagos Life)
    if (busyNow) {
      void act(activityId);
      return null;
    }
    if (!entry.use.ok) {
      notify(`${entry.activity.label} needs ${entry.use.missing}.`);
      return null;
    }
    return () => void act(activityId);
  };

  // a new player's first look at their home: a welcome snackbar, once
  const welcomeText =
    open === 'welcome' && type ? `Welcome home, ${life.firstName}. ${type.description} Make it yours.` : null;
  useEffect(() => {
    if (welcomeText) notify(welcomeText, { id: 'house-welcome', duration: 7000 });
  }, [welcomeText]);
  const rentDue = home ? new Date(home.rent.dueAt) : null;
  const dueText = rentDue ? londonDay(rentDue) : '';
  const wallet = reply?.walletPence ?? 0;
  const sellBack = (thing: OwnedThing) => (thing.starter ? 0 : Math.floor(thing.pricePence * SELL_BACK_SHARE));

  return (
    <div
      className={`venue-screen house-screen${buyMode ? ' buying' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={type ? type.label : 'Your place'}
    >
      {home && (
        <HouseView
          room={home.room}
          placed={draft}
          character={toPreviewCharacter(life.appearance, life.firstName)}
          dataSaver={dataSaver}
          editing={buyMode}
          selectedId={selected}
          onSelect={(id) => {
            setSelected(id);
            setSelling(false);
          }}
          onDrop={drop}
          revision={revision}
          onThing={useThing}
          onBlocked={() => notify("Can't walk there: the furniture is in the way. Move things in Buy mode.")}
          busy={busyNow}
          lift={buyMode ? (catalogue || selected ? 0.42 : 0.12) : homeTab ? 0.32 : 0.18}
          people={buyMode ? [] : visitors}
        />
      )}
      {failure && (
        <div className="house-failure">
          <ErrorNotice error={failure} onRetry={home ? undefined : load} />
        </div>
      )}
      {!home && !failure && (
        <p className="house-hint" role="status">
          Opening the door…
        </p>
      )}
      {home && type && buyMode && (
        <>
          <div className="buy-top">
            <span className="buy-title">Buy mode</span>
            <span className="buy-wallet" aria-label="Wallet">
              {formatPence(wallet)}
            </span>
            <button type="button" className="buy-done" aria-label="Leave Buy mode" onClick={leaveBuy}>
              <Check size={18} aria-hidden="true" /> Done
            </button>
          </div>
          {selectedThing && selectedItem && selectedOwned ? (
            <SelectedBar
              item={selectedItem}
              line={`${
                sellBack(selectedOwned)
                  ? `Sells for ${formatPence(sellBack(selectedOwned))}`
                  : 'Came with the home: nothing back'
              } · Drag it to move it`}
            >
              {selling ? (
                <div className="buy-actions" role="group" aria-label="Confirm the sale">
                  <span>
                    {sellBack(selectedOwned) ? `${formatPence(sellBack(selectedOwned))} back?` : 'Nothing back?'}
                  </span>
                  <button type="button" className="danger" onClick={() => sell(selectedOwned)} disabled={busy}>
                    Yes, sell
                  </button>
                  <button type="button" onClick={() => setSelling(false)}>
                    No
                  </button>
                </div>
              ) : (
                <div className="buy-actions">
                  <button type="button" onClick={turn} disabled={busy}>
                    ⟳ Turn
                  </button>
                  <button type="button" onClick={store} disabled={busy}>
                    ▣ Store
                  </button>
                  <button type="button" onClick={() => setSelected(null)}>
                    Close
                  </button>
                  <button type="button" className="danger" onClick={() => setSelling(true)} disabled={busy}>
                    Sell
                  </button>
                </div>
              )}
            </SelectedBar>
          ) : catalogue ? (
            <Catalogue
              inRoom={draft}
              stored={stored}
              busy={busy}
              firstShelf="sleep"
              price={(item) => ({ label: `Buy ${formatPence(item.pricePence)}`, short: item.pricePence > wallet })}
              onPick={setSelected}
              onPlace={(thing) => place(thing)}
              onBuy={(item) => void buy(item)}
              onHide={() => setCatalogue(false)}
              hint="Buy something and it goes straight into the room. Sell anything back for half its price."
            />
          ) : (
            <button type="button" className="catalogue-open" onClick={() => setCatalogue(true)}>
              <LayoutGrid size={18} aria-hidden="true" /> Catalogue
            </button>
          )}
        </>
      )}
      {home &&
        type &&
        !buyMode &&
        (folded ? (
          <div className="venue-pill">
            <button type="button" className="venue-pill-name" onClick={() => setFolded(false)}>
              🏠 {type.label}
            </button>
            <button type="button" aria-label="Buy mode" onClick={() => onMode('shop')}>
              <ShoppingBag size={18} />
            </button>
            <button type="button" aria-label="Back to the map" onClick={onClose}>
              <MapIcon size={18} />
            </button>
          </div>
        ) : (
          <section className="venue-bar" aria-label="Your home">
            <header className="venue-head">
              <PersonPicture person={{ firstName: life.firstName, look: life.appearance }} />
              <div className="venue-where">
                <h2>
                  🏠 {type.label} <small>· {type.area}</small>
                </h2>
                <p>Tap the floor to walk, tap a thing to use it</p>
              </div>
              <div className="venue-actions">
                <button type="button" aria-label="Buy mode" onClick={() => onMode('shop')}>
                  <ShoppingBag size={17} />
                </button>
                <button type="button" aria-label="Back to the map" onClick={onClose}>
                  <MapIcon size={17} />
                </button>
                <button
                  type="button"
                  aria-label="Fold the bar"
                  onClick={() => {
                    setHomeTab(null);
                    setFolded(true);
                  }}
                >
                  <ChevronDown size={17} />
                </button>
              </div>
            </header>
            {onAct && !atHome && (
              <p className="house-use-away">
                You are out. Come home to use your things.
                {onGoHome && (
                  <button type="button" className="house-button" onClick={() => void onGoHome()} disabled={using}>
                    🚶 Walk home
                  </button>
                )}
              </p>
            )}
            <nav className="venue-tabs" aria-label="At home">
              {homeTab && (
                <button type="button" className="venue-tab-close" aria-label="Close" onClick={() => setHomeTab(null)}>
                  <ChevronDown size={16} />
                </button>
              )}
              <button
                type="button"
                aria-pressed={homeTab === 'do'}
                onClick={() => setHomeTab(homeTab === 'do' ? null : 'do')}
              >
                <span aria-hidden="true">✨</span> Things to do
              </button>
              <button
                type="button"
                aria-pressed={homeTab === 'rent'}
                className={owed ? 'owed' : undefined}
                onClick={() => setHomeTab(homeTab === 'rent' ? null : 'rent')}
              >
                <span aria-hidden="true">🔑</span> {home.owned ? 'Yours' : `Rent${owed ? ' · owed' : ''}`}
              </button>
              {inside.length > 0 && (
                <button
                  type="button"
                  aria-pressed={homeTab === 'guests'}
                  onClick={() => setHomeTab(homeTab === 'guests' ? null : 'guests')}
                >
                  <span aria-hidden="true">👥</span> Guests · {inside.length}
                </button>
              )}
            </nav>
            {homeTab === 'guests' && (
              <ul className="house-guests" aria-label="Guests">
                {inside.map((guest) => (
                  <li key={guest.visitId}>
                    <PersonPicture person={guest} />
                    <span>{guest.username ? `@${guest.username}` : guest.firstName}</span>
                    {onSendHome && (
                      <button type="button" className="house-button" onClick={() => onSendHome(guest.visitId)}>
                        👋 Say goodbye
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {homeTab === 'do' && onAct && needs && (
              <div className="venue-cards" role="group" aria-label="Use your things">
                {uses.map(({ activity, use }) => (
                  <button
                    key={activity.id}
                    type="button"
                    className="venue-card"
                    disabled={!use.ok || !atHome || using}
                    onClick={() => {
                      setHomeTab(null);
                      void act(activity.id);
                    }}
                  >
                    <span className="venue-card-top">
                      <span className="venue-card-icon" aria-hidden="true">
                        {activity.icon}
                      </span>
                      <span className="venue-card-meta">
                        <small>⏱ {activity.seconds}s</small>
                        <em>Free</em>
                      </span>
                    </span>
                    <strong>{activity.label}</strong>
                    <small className="venue-card-note">
                      {use.ok
                        ? `${NEEDS[use.using.need!].icon} +${use.restores} · ${use.using.label}${use.also ? ` + ${use.also.label.toLowerCase()}` : ''}`
                        : `Needs ${use.missing}`}
                    </small>
                  </button>
                ))}
              </div>
            )}
            {homeTab === 'rent' && (
              <div className={home.rent.owedPence > 0 ? 'house-rent owed' : 'house-rent'} aria-label="Rent">
                <span>
                  {home.owned ? (
                    <>
                      <b>Yours</b> no rent, ever · sells back for {formatPence(home.sellPence)} (Phone → Houses)
                    </>
                  ) : (
                    <>
                      <b>Rent</b> {formatPence(home.rent.weeklyPence)} a week · next due {dueText}
                    </>
                  )}
                </span>
                {home.rent.owedPence > 0 ? (
                  <>
                    <span className="house-owed">
                      <b>Owed</b> {formatPence(home.rent.owedPence)}
                      {home.rent.missedWeeks > 0 &&
                        ` · ${home.rent.missedWeeks} week${home.rent.missedWeeks === 1 ? '' : 's'} missed (evicted after 3)`}
                    </span>
                    <button
                      type="button"
                      className="house-button save"
                      onClick={payRent}
                      disabled={busy || home.rent.owedPence > wallet}
                      title={home.rent.owedPence > wallet ? 'Not enough money' : undefined}
                    >
                      Pay rent {formatPence(home.rent.owedPence)}
                    </button>
                  </>
                ) : (
                  <span className="house-paid">Paid up</span>
                )}
                <span>
                  <b>Wallet</b> {formatPence(wallet)}
                </span>
              </div>
            )}
          </section>
        ))}
    </div>
  );
}
